import React, { useState } from 'react';
import { BellRing, BellOff } from 'lucide-react';
import Button from './ui/Button';
import { useNotifications, pushSupported } from '../hooks/useNotifications';
import { useToast } from './ui/ToastProvider';
import styles from './PushToggle.module.css';

/**
 * Turning push notifications on, from anywhere.
 *
 * The one control, used in both places it appears: the Notifications page, and
 * the bell dropdown in the top bar — which is on every page in the app. That is
 * the actual fix for "enabling notifications doesn't work depending on which
 * page you're on": there was only ever one place to do it, so from anywhere
 * else it could not be done at all.
 *
 * It owns no state of its own. Permission, subscription and the resulting
 * on/off/denied/unsupported reading all live in NotificationsProvider, which is
 * mounted above the router — so two of these on screen at once cannot disagree,
 * and neither can two tabs.
 */

const COPY = {
  unsupported: 'This browser doesn\'t support notifications.',
  denied: 'Notifications are blocked for this site in your browser settings. Allow them there and this will update on its own.',
  off: 'Get alerted on this device, even when the app is closed.',
  on: 'This device is getting notifications.',
};

function PushToggle({ variant = 'card' }) {
  const { pushState, enablePush, disablePush, sendTestPush } = useNotifications();
  const { show: showToast } = useToast();
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    setBusy(true);
    try {
      if (pushState === 'on') {
        await disablePush();
        showToast({ variant: 'success', message: 'Notifications turned off for this device.' });
      } else {
        // Straight off the click — Notification.requestPermission() is only
        // honoured inside a user gesture, and anything awaited before it can
        // cost us that gesture in some browsers.
        await enablePush();
        showToast({
          variant: 'success',
          title: 'Notifications on',
          message: 'You\'ll hear from us even when the app is closed.',
        });
      }
    } catch (err) {
      showToast({ variant: 'danger', title: 'Couldn\'t change that', message: err.message });
    } finally {
      setBusy(false);
    }
  };

  // Nothing to offer, and saying so in a dropdown every time the bell is opened
  // is noise. The card variant still explains itself.
  if (pushState === 'unsupported' && variant === 'compact') return null;
  if (pushState === 'on' && variant === 'compact') return null;

  if (variant === 'compact') {
    return (
      <button type="button" className={styles.compact} onClick={toggle} disabled={busy || pushState === 'denied'}>
        <BellRing size={15} aria-hidden="true" />
        <span className={styles.compactText}>
          {pushState === 'denied' ? 'Notifications are blocked in your browser' : 'Turn on notifications'}
        </span>
      </button>
    );
  }

  return (
    <div className={styles.card}>
      <div className={[styles.icon, pushState === 'on' ? styles.iconOn : ''].filter(Boolean).join(' ')}>
        {pushState === 'on' ? <BellRing size={20} /> : <BellOff size={20} />}
      </div>
      <div className={styles.body}>
        <div className={styles.title}>Notifications</div>
        <div className={styles.copy}>{COPY[pushState]}</div>
      </div>
      <div className={styles.actions}>
        {pushState === 'on' && (
          <Button variant="secondary" size="sm" onClick={() => sendTestPush().catch(() => {})}>
            Send test
          </Button>
        )}
        {pushSupported() && pushState !== 'denied' && (
          <Button
            variant={pushState === 'on' ? 'secondary' : 'primary'}
            loading={busy}
            onClick={toggle}
          >
            {pushState === 'on' ? 'Turn off' : 'Turn on'}
          </Button>
        )}
      </div>
    </div>
  );
}

export default PushToggle;
